/*
We want to have:
- Agenda for the next meeting, not to forget to discuss - checkbox items list
- Action items - for both?
- Meeting notes log of every meeting every time, grouped by date

AI part
- AI notes summary
- Perf review AI generation
*/

import React, { useState, useEffect, useRef } from 'react';

import {
    Text,
    VStack,
    Divider,
    Box,
    HStack,
    Button,
    Textarea,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Input,
    Spacer,
    useDisclosure
} from '@chakra-ui/react';

import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';

import Const from '../Constants';
import { fsDateToString, sortByTitle, handleUpdateMeeting } from '../Utils';

import { useMeetingsStore } from '../Stores/MeetingsStore';

import { SortableCheckbox } from '../Atoms/EditableCheckbox';
import ConfirmDeleteDialog from '../Components/ConfirmDeleteDialog';

const MeetingNoteTextarea = ({ auth, meetingNote }) => {
    const meetings = useMeetingsStore((state) => state.meetings);
    const setMeetings = useMeetingsStore((state) => state.setMeetings);
    const currentMeeting = useMeetingsStore((state) => state.currentMeeting);
    const setCurrentMeeting = useMeetingsStore((state) => state.setCurrentMeeting);

    // Internal state
    const [internalMeetingNote, setInternalMeetingNote] = useState(meetingNote);

    // Update local value when initialValue changes (from parent component re-render)
    useEffect(() => {
        setInternalMeetingNote(meetingNote);
    // eslint-disable-next-line
    }, [meetingNote]);

    const handleChange = (event) => {
        const value = event.target.value;

        if(value !== internalMeetingNote.body) {
            let copyInternalMeetingNote = JSON.parse(JSON.stringify(internalMeetingNote));
            copyInternalMeetingNote.body = value;
            setInternalMeetingNote(copyInternalMeetingNote);
        }
    };

    const handleBlur = () => {
        if (internalMeetingNote.body !== meetingNote.body) {
            handleAddOrUpdateMeetingNotes();
        }
    };

    const handleAddOrUpdateMeetingNotes = async () => {
        // -- Update server state
        if(!internalMeetingNote.id) { // Add new note case
            const idToken = await auth.currentUser.getIdToken();
              const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsNotesPath;
              const response = await fetch(theURL, {
                method: Const.HttpPost,
                headers: new Headers({
                  Authorization: idToken,
                  Accept: Const.AppJson,
                  [Const.ContentType]: Const.AppJson
                }),
                body: JSON.stringify({
                  meetingId: internalMeetingNote.meetingId,
                  body: internalMeetingNote.body
                })
              });

              // Check if the request was successful
              if(!response.ok) {
                throw new Error('Failed to add meeting note');
              }

              // Parse the added note from the response
              const addedMeetingNote = await response.json();

              // Update the id as well
              let copyInternalMeetingNote = JSON.parse(JSON.stringify(internalMeetingNote));
              copyInternalMeetingNote.id = addedMeetingNote.id;
              setInternalMeetingNote(copyInternalMeetingNote);

              // State is not being updated instantly, even if we do await so update the object here so we can use id in this function.
              internalMeetingNote.id = addedMeetingNote.id;
        }
        else { // Update note case
            const idToken = await auth.currentUser.getIdToken();
              const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsNotesPath;
              const response = await fetch(theURL, {
                method: Const.HttpPatch,
                headers: new Headers({
                  Authorization: idToken,
                  Accept: Const.AppJson,
                  [Const.ContentType]: Const.AppJson
                }),
                body: JSON.stringify({
                  meetingNoteId: internalMeetingNote.id,
                  body: internalMeetingNote.body
                })
              });

              // Check if the request was successful
              if(!response.ok) {
                throw new Error('Failed to update meeting note');
              }

              // Note already got an id as this is an update, no need to take care of the id.
        }

        // Match the single note we just saved. Match by server id when it exists,
        // otherwise by the stable clientId — never a bare `id === undefined`, which
        // would also match other unsaved notes (e.g. the blank today note) and
        // overwrite them with this note's content.
        const isSameNote = (n) =>
            (internalMeetingNote.id !== undefined && n.id === internalMeetingNote.id) ||
            (internalMeetingNote.clientId !== undefined && n.clientId === internalMeetingNote.clientId);

        // -- Update local state
        // - Update current one
        let copyCurrentMeeting = JSON.parse(JSON.stringify(currentMeeting));

        copyCurrentMeeting.data.meetingNotes.forEach((meetingNote, index) => {
            if (isSameNote(meetingNote)) {
                  copyCurrentMeeting.data.meetingNotes[index] = JSON.parse(JSON.stringify(internalMeetingNote));
            }
        });

        setCurrentMeeting(copyCurrentMeeting);

        // - Update the whole data

        let copyMeetingsData = JSON.parse(JSON.stringify(meetings));

        copyMeetingsData.forEach((meeting, index) => {
            if (meeting.id === currentMeeting.id) {
              copyMeetingsData[index].data.meetingNotes.forEach((meetingNote, index2) => {
                    if(isSameNote(meetingNote)) {
                          copyMeetingsData[index].data.meetingNotes[index2] = JSON.parse(JSON.stringify(internalMeetingNote));
                    }
                });
            }
        });

        setMeetings(copyMeetingsData);
      };

    return (
        <Textarea
            value={internalMeetingNote.body}
            onChange={handleChange}
            onBlur={handleBlur}
            fontSize={Const.MD}
            flex={'1'}
            h={'100%'}
            resize={'none'}
            borderColor={'gray.300'}
            placeholder={'Enter meeting notes here'}
            _placeholder={{ color: 'gray.500' }}
            _hover={{ cursor: 'text' }}
        />
    );
};

const MeetingsDetails = ({ auth }) => {
  const meetings = useMeetingsStore((state) => state.meetings);
  const setMeetings = useMeetingsStore((state) => state.setMeetings);
  const currentMeeting = useMeetingsStore((state) => state.currentMeeting);
  const setCurrentMeeting = useMeetingsStore((state) => state.setCurrentMeeting);
  const addCheckable = useMeetingsStore((state) => state.addCheckable);
  const updateCheckable = useMeetingsStore((state) => state.updateCheckable);
  const deleteCheckable = useMeetingsStore((state) => state.deleteCheckable);
  const moveCheckable = useMeetingsStore((state) => state.moveCheckable);

  // Used as a value for the title input
  const [title, setTitle] = useState(currentMeeting ? currentMeeting.title : '');
  const titleInputRef = useRef(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleConfirmDelete = async () => {
    const meetingId = currentMeeting?.id;
    onClose();
    if (meetingId) {
      await handleDeleteMeeting(meetingId);
    }
  };

  // This is a state that describes if there is any checkable being edited right now.
  // This is needed to fix a bug when people will hit add button while editing a checkable.
  const [globalCheckableEditState, setGlobalCheckableEditState] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if(currentMeeting) {
      if(currentMeeting.title !== title) {
        setTitle(currentMeeting.title);
      }

      // If the current item title is empty set the focus on title input
      if(currentMeeting.title === '' && titleInputRef.current) {
        titleInputRef.current.focus();
      }
    }
  // eslint-disable-next-line
  }, [currentMeeting]);

  async function handleDeleteMeeting(meetingId) {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsPath;
      await fetch(theURL, {
        method: Const.HttpDelete,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson
        }),
        body: JSON.stringify({
          meetingId
        })
      });

      // Delete from meetings list.
      setMeetings(meetings.filter(obj => obj.id !== meetingId));

      // Object is removed, make sure the details view is gone for it.
      setCurrentMeeting(null);

    } catch (error) {
      console.error(error.message);
    }
}

const handleBlurTitle = async () => {
    // Update the title on backend
    if(currentMeeting && currentMeeting.title !== title) {
        await handleUpdateMeeting(auth, currentMeeting.id, title, null);

        // Update current one as well
        let copyCurrentMeeting = JSON.parse(JSON.stringify(currentMeeting));
        copyCurrentMeeting.title = title;
        setCurrentMeeting(copyCurrentMeeting);

        // Update the data as well
        const copyMeetingsData = JSON.parse(JSON.stringify(meetings));
        const item = copyMeetingsData.find(obj => obj.id === currentMeeting.id);
        item.title = title;
        sortByTitle(copyMeetingsData);
        setMeetings(copyMeetingsData);
    }
};

  function handleCheckableAdd(checkableType) {
    // Do not start adding a new one while edit is in progress, we want it to commit successfully.
    if(globalCheckableEditState) {
      return;
    }
    addCheckable(auth, checkableType);
  }

  // Reorder handler for one list. `items` is the visible (filtered, ordered) list.
  const handleDragEnd = (checkableType, items) => ({ active, over }) => {
    if (!over || active.id === over.id) return;

    // Replay dnd-kit's arrayMove so drag direction is respected (dragging down
    // lands after the target, up lands before it), then read the moved item's
    // neighbors from the reordered list.
    const ids = items.map((i) => i.id);
    const oldIndex = ids.indexOf(active.id);
    const overIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || overIndex === -1) return;

    const newIds = arrayMove(ids, oldIndex, overIndex);
    const pos = newIds.indexOf(active.id);
    const beforeId = pos > 0 ? newIds[pos - 1] : null;
    const afterId = pos < newIds.length - 1 ? newIds[pos + 1] : null;

    moveCheckable(auth, { type: checkableType, itemId: active.id, beforeId, afterId });
  };

  // Visible (non-done) items per list, already rank-sorted by the store.
  const agendaItems = (currentMeeting.data?.agenda || []).filter(i => i.status !== Const.sDone);
  const actionItems = (currentMeeting.data?.actionItems || []).filter(i => i.status !== Const.sDone);

  const renderCheckableList = (checkableType, items) => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd(checkableType, items)}
    >
      <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableCheckbox
            key={item.id}
            value={item.title}
            object={item}
            setGlobalCheckableEditState={setGlobalCheckableEditState}
            onChange={updateCheckable.bind(null, auth)}
            onDelete={deleteCheckable.bind(null, auth)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );

  return (
    <VStack
        width={'100%'}
        h={'100vh'}
        alignItems={'top'}
        paddingLeft={6}
        paddingRight={2}
        paddingTop={2}
        paddingBottom={2}
    >
    <HStack>
    <Input
        ref={titleInputRef}
        fontSize={Const.XL}
        fontWeight={Const.Bold}
        value={title}
        border={Const.sNone}
        marginLeft={-4}
        onChange={(event) => {
            setTitle(event.target.value);
        }}
        onBlur={() => {
            handleBlurTitle();
        }}
        onKeyDown={(event) => {
            if (event.key === Const.sEnter) {
                // Remove focus
                titleInputRef.current.blur();
                event.preventDefault(); // Prevents any default action
            }
        }}
    />
    <Spacer />
    <Button
        leftIcon={<DeleteIcon/>}
        iconSpacing={0}
        size={Const.SM}
        height={'30px'}
        background={Const.Gray69}
        color={Const.Gray165}
        _hover={{ background: Const.Red600 }}
        fontSize={Const.XS}
        onClick={onOpen}
    />
    </HStack>

    <HStack alignItems={'top'}>

        <VStack
            width={'100%'}
            alignItems={'top'}
        >
            <Text
                align={Const.Left}
                fontSize={Const.MD}
                fontWeight={Const.Bold}
                w={'76px'}
                color={Const.Gray165}
                _hover={{ cursor: Const.sDefault }}
            >
                Agenda
            </Text>
            <Divider my={1} borderColor={Const.Gray100} />
            {renderCheckableList(Const.sAgenda, agendaItems)}
            <Button
                leftIcon={<AddIcon/>}
                size={Const.SM}
                minH={'30px'}
                width={'80px'}
                background={Const.Gray31}
                color={Const.Green500}
                _hover={{ background: Const.Gray69 }}
                onClick={() => {
                  setTimeout(() => {
                    handleCheckableAdd(Const.sAgenda);
                  }, 0); // Postpone the execution
                }}
            >
                Agenda
            </Button>

        </VStack>

        <VStack
            width={'100%'}
            alignItems={'top'}
        >
            <Text
                align={Const.Left}
                fontSize={Const.MD}
                fontWeight={Const.Bold}
                color={Const.Gray180}
            >
                Action items
            </Text>
            <Divider my={1} borderColor={Const.Gray100} />
            {renderCheckableList(Const.sAction, actionItems)}
            <Button
                leftIcon={<AddIcon/>}
                size={Const.SM}
                minH={'30px'}
                width={'110px'}
                background={Const.Gray31}
                color={Const.Green500}
                _hover={{ background: Const.Gray69 }}
                onClick={() => {
                  setTimeout(() => {
                    handleCheckableAdd(Const.sAction)
                  }, 0); // Postpone the execution
                }}
            >
                Action item
            </Button>
        </VStack>

    </HStack>

    <Box h={4}/>

    <Text
        align={Const.Left}
        fontSize={Const.MD}
        fontWeight={Const.Bold}
        color={Const.Gray180}
    >
        Meeting notes
    </Text>
    <Divider my={1} borderColor={Const.Gray100} />
    <Box h={'100%'} overflowY={'auto'} maxH={'100%'}>
        <Accordion allowMultiple h={'100%'} defaultIndex={[0]}>
            {
                currentMeeting.data && currentMeeting.data.meetingNotes && currentMeeting.data.meetingNotes.map((item) => {
                    return (
                        <AccordionItem key={item.id ?? item.clientId} border={Const.sNone}>
                            <AccordionButton>
                                <Box as='span' flex='1' textAlign={Const.Left} fontSize={Const.SM} fontWeight={Const.Bold} color={Const.Gray180}>
                                    {fsDateToString(item.date)}
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4} height={'36vh'}>
                                <MeetingNoteTextarea
                                    auth={auth}
                                    meetingNote={item}
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    );
                })
            }
        </Accordion>
    </Box>

    <ConfirmDeleteDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        title={'Delete meeting'}
        body={"Are you sure you want to delete this meeting? This can't be undone."}
    />

    </VStack>
    );
};

export default MeetingsDetails;
